import React, { SyntheticEvent, useState, MutableRefObject } from 'react'

import Dropdown from 'components/Dropdown'
import { useToast } from 'containers/ToastManager'
import { useModalContext } from 'containers/ModalManager/ModalContext'
import { ReactComponent as FolderClosed } from 'icons/folder-closed-16.svg'
import { ReactComponent as FolderOpen } from 'icons/folder-open-16.svg'
import { useMoveShareToProject } from 'modules/projects/operations'
import { ShareDropPayload } from 'modules/projects/components/MenuLink/useMenuShareDrop'
import { ConfirmDraftsMoveModal } from 'modules/projects/modals'

import { ProjectId, ProjectLink } from '../../types'
import { DroppableLinkItem, ActionDropdown } from '../Item'

import { RenameInlineEditor } from '../InlineEditor'

export interface ProjectProps extends ProjectLink {
  onDelete: (id: ProjectId, name: string) => void
  userCanEdit: boolean
  userCanAdminister: boolean
  workspaceId: string
  refToLast?: React.MutableRefObject<HTMLElement>
  setEditedItem: (value: string) => void
  autofocusLastCreated?: MutableRefObject<boolean>
}

export const ProjectListItem: React.FC<ProjectProps> = props => {
  const {
    link,
    onDelete,
    project,
    userCanEdit,
    userCanAdminister,
    workspaceId,
    refToLast,
    setEditedItem,
  } = props
  const { showToast } = useToast()
  const { showModal } = useModalContext()
  const [isRenaming, setIsRenaming] = useState(false)

  const [moveShare] = useMoveShareToProject({
    onCompleted: project =>
      showToast(`Your document was moved to "${project.name}"`),
    onError: message => showToast(message, 'negative'),
  })

  const closeAllPopups = () => {
    try {
      const clickOutside = new MouseEvent('click')
      const keyboardEvent = new KeyboardEvent('keydown', {
        key: 'Escape',
        keyCode: 27,
      } as KeyboardEventInit)
      document.body.dispatchEvent(clickOutside)
      document.body.dispatchEvent(keyboardEvent)
    } catch (e) {
      // empty catch to prevent IE11 from blowing up on this function
    }
  }

  const handleDeleteOnClick = (event: SyntheticEvent) => {
    event.preventDefault()
    closeAllPopups()
    onDelete(project.shortId, project.name)
  }

  const onDropped = ({ shareId, share }: ShareDropPayload) => {
    if (!share) {
      return
    }

    // Warn the user the document will become public
    if (share.project?.type === 'PERSONAL_DRAFTS') {
      showModal(ConfirmDraftsMoveModal, {
        share,
        newProject: project,
      })
    } else {
      moveShare({ shareId, projectId: project.identifier })
    }
  }

  const dropdownContent =
    userCanEdit || userCanAdminister ? (
      <>
        <Dropdown.Header>Project</Dropdown.Header>
        <Dropdown.Item onClick={() => setIsRenaming(!isRenaming)}>
          Rename&hellip;
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item intent="negative" onClick={handleDeleteOnClick}>
          Delete&hellip;
        </Dropdown.Item>
      </>
    ) : null

  const toggleEditor = () => setIsRenaming(!isRenaming)
  const onCompleted = () => {
    setEditedItem(project.identifier)
  }

  if (isRenaming) {
    return (
      <RenameInlineEditor
        projectId={project.identifier}
        projectName={project.name}
        workspaceId={workspaceId}
        placeholder={project.name || 'Folder'}
        toggleEditor={toggleEditor}
        onCompleted={onCompleted}
      />
    )
  }

  return (
    <DroppableLinkItem
      $activeIcon={FolderOpen}
      icon={FolderClosed}
      to={link}
      text={project.name}
      onDropped={onDropped}
      action={
        dropdownContent && (
          <ActionDropdown text="Rename & More…" content={dropdownContent} />
        )
      }
      ref={refToLast}
      aria-label={`Link to ${project.name}`}
    />
  )
}
